<template>
    <div class="orb-business-panel">
    <div class="border">
      <div class="header">
        <div class="header-text">
          <div class="pt-kicker">综合监控</div>
          <p class="card-title">{{ title }}</p>
        </div>
        <button class="close" @click="closeClick">&times;</button>
      </div>

      <div v-if="query" class="query-summary">
        <div class="summary-main">
          <span class="summary-station">{{ query.station.name }}</span>
          <span class="summary-metric">{{ query.metric.label }}</span>
          <strong>{{ query.current }} {{ query.metric.unit }}</strong>
        </div>
        <div class="summary-meta">
          <span>状态：{{ query.status }}</span>
          <span>趋势：{{ query.trend >= 0 ? '上升' : '下降' }} {{ Math.abs(query.trend) }}</span>
          <span>更新时间：{{ query.time }}</span>
        </div>
      </div>

      <div class="charts-wrapper">
        <div ref="chart1" class="chart chart-top-left"></div>
        <div ref="chart2" class="chart chart-top-right"></div>
        <div ref="chart3" class="chart chart-bottom-left"></div>
        <div ref="chart4" class="chart chart-bottom-right"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { buildMonitoringSnapshot, createMonitoringSeries } from '@/services/demoDataService.js'
import { fetchStationSeries, fetchSupplyDemandRegions, toMonitoringChartData, toSupplyDemandRows } from '@/services/businessDataService.js'

export default {
  name: "monitoringLabel",
  props: {
    title: {
      type: String,
      default: '综合监控'
    },
    query: {
      type: Object,
      default: null
    },
  },
  computed: {
    displayStationName() {
      return this.query && this.query.station ? this.query.station.name : '古贤'
    }
  },
  data() {
    return {
      charts: {
        c1: null,
        c2: null,
        c3: null,
        c4: null
      },
      // 存储模拟数据（你可以改成从 API 获取并调用 updateCharts）
      times: [],            // 时间刻度
      inflow: [],           // 流入（m³/s）
      outflow: [],          // 流出（m³/s）
      reservoirLevel: [],   // 水库水位（m）
      allocationBySector: { // 柱状图数据（万m³）
        categories: ['农业', '工业', '生活', '生态', '其他'],
        values: []
      },
      allocationPie: []     // 饼图：水源或用途占比
    }
  },
  mounted() {
    this._loadBusinessData()
    window.addEventListener('resize', this._resizeCharts)
    // 若要定时刷新模拟数据，取消以下注释：
    // this.timer = setInterval(() => { this._generateMockData(); this._updateCharts(); }, 5000)
  },
  destroyed() {
    this._disposeCharts()
    window.removeEventListener('resize', this._resizeCharts)
    // clearInterval(this.timer)
  },
  methods: {
    closeClick() {
      this.$emit('infoClose')
    },

    async _loadBusinessData() {
      if (this.query && this.query.station && this.query.metric) {
        this._generateMockData()
      } else {
        const [seriesPayload, regionsPayload] = await Promise.all([
          fetchStationSeries('guxian-reservoir', 24),
          fetchSupplyDemandRegions(),
        ])
        const chartData = toMonitoringChartData(seriesPayload)
        this.times = chartData.times
        this.inflow = chartData.inflow
        this.outflow = chartData.outflow
        this.reservoirLevel = chartData.reservoirLevel

        const rows = toSupplyDemandRows(regionsPayload.regions || [])
        this.allocationBySector.values = rows.slice(0, 5).map(row => Math.round(row.demand / 10000))
        this.allocationPie = [
          { name: '古贤水库', value: 54 },
          { name: '本地蓄水', value: 22 },
          { name: '区间来水', value: 13 },
          { name: '再生水', value: 7 },
          { name: '雨洪利用', value: 4 },
        ]
      }
      this._initCharts()
    },

    /* =========== 数据与初始化 =========== */
    _generateMockData() {
      if (this.query && this.query.station && this.query.metric) {
        const snapshot = buildMonitoringSnapshot(this.query.station, this.query.metric)
        const series = createMonitoringSeries(snapshot)
        this.times = series.times
        this.inflow = series.inflow
        this.outflow = series.outflow
        this.reservoirLevel = series.reservoirLevel
        this.allocationBySector.values = series.allocationValues
        this.allocationPie = series.allocationPie
        return
      }

      // 生成最近 24 小时每小时刻度
      const now = new Date()
      const times = []
      for (let i = 23; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 3600 * 1000)
        const label = `${d.getHours()}:00`
        times.push(label)
      }

      // 模拟：流入/流出（m3/s）波动，水库水位（m），总体水量分配（万m3）
      const inflow = []
      const outflow = []
      const reservoirLevel = []
      let baseIn = 120 // 基准流入
      let baseOut = 90
      let baseLevel = 56 // m
      for (let i = 0; i < 24; i++) {
        // 用正弦 + 随机来模拟日内波动
        const hourFactor = Math.sin((i / 24) * Math.PI * 2)
        const inVal = Math.max(20, Math.round(baseIn + hourFactor * 30 + (Math.random() - 0.5) * 10))
        const outVal = Math.max(10, Math.round(baseOut + hourFactor * 20 + (Math.random() - 0.5) * 8))
        const levelVal = Math.round((baseLevel + (inVal - outVal) * 0.02 + (Math.random() - 0.5) * 0.3) * 10) / 10

        inflow.push(inVal)
        outflow.push(outVal)
        reservoirLevel.push(levelVal)
      }

      // 柱状图：当日按行业分配总量（万m3）
      const allocationValues = [
        Math.round(120 + Math.random() * 30), // 农业
        Math.round(70 + Math.random() * 20),  // 工业
        Math.round(60 + Math.random() * 25),  // 生活
        Math.round(40 + Math.random() * 15),  // 生态
        Math.round(10 + Math.random() * 8)    // 其他
      ]

      // 饼图：按水源构成或用途占比（百分比）
      const pie = [
        { name: '地表水', value: Math.round(50 + Math.random() * 10) },
        { name: '地下水', value: Math.round(20 + Math.random() * 8) },
        { name: '再生水', value: Math.round(10 + Math.random() * 6) },
        { name: '调水', value: Math.round(10 + Math.random() * 6) },
        { name: '雨水利用', value: Math.round(5 + Math.random() * 4) }
      ]
      // 归一化使和为100
      const sum = pie.reduce((s, p) => s + p.value, 0)
      pie.forEach(p => { p.value = Math.round((p.value / sum) * 100) })

      this.times = times
      this.inflow = inflow
      this.outflow = outflow
      this.reservoirLevel = reservoirLevel
      this.allocationBySector.values = allocationValues
      this.allocationPie = pie
    },

    _initCharts() {
      // init echarts instances
      this.charts.c1 = echarts.init(this.$refs.chart1)
      this.charts.c2 = echarts.init(this.$refs.chart2)
      this.charts.c3 = echarts.init(this.$refs.chart3)
      this.charts.c4 = echarts.init(this.$refs.chart4)

      this._setChart1() // 流入 vs 流出 折线（带渐变面积）
      this._setChart2() // 水库水位 折线
      this._setChart3() // 按行业分配 柱状图
      this._setChart4() // 饼图：水源占比
    },

    _disposeCharts() {
      Object.values(this.charts).forEach(ch => {
        if (ch && !ch.isDisposed && typeof ch.dispose === 'function') ch.dispose()
      })
    },

    _resizeCharts() {
      Object.values(this.charts).forEach(ch => {
        try { ch && ch.resize() } catch (e) { /* ignore */ }
      })
    },

    _updateCharts() {
      // 如果数据更新，调用 setOption 更新数据
      this._setChart1(true)
      this._setChart2(true)
      this._setChart3(true)
      this._setChart4(true)
    },

    /* =========== 各图表 option =========== */
    _setChart1(update = false) {
      // Top-left: 流入 vs 流出（折线，带渐变面积）
      const option = {
        backgroundColor: 'transparent',
        title: {
          text: `${this.displayStationName}入库/出库（m³/s）`,
          left: 'left',
          textStyle: { color: '#9bdcff', fontSize: 12 }
        },

        tooltip: { trigger: 'axis' },
        legend: { data: ['流入', '流出'], right: 10, textStyle: { color: '#cfeefc' } },
        grid: {
          left: '8%',
          right: '6%',
          bottom: '14%',
          top: '22%'
        },
        xAxis: {
          type: 'category',
          data: this.times,
          axisLine: { lineStyle: { color: '#123844' } },
          axisLabel: { color: '#9bdcff', interval: 2 }
        },
        yAxis: {
          type: 'value',
          name: 'm³/s',
          axisLine: { lineStyle: { color: '#123844' } },
          splitLine: { lineStyle: { type: 'dashed', color: '#072028' } },
          axisLabel: { color: '#9bdcff' }
        },
        series: [
          {
            name: '流入',
            type: 'line',
            data: this.inflow,
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(0,242,248,0.35)' },
                { offset: 1, color: 'rgba(0,60,80,0.02)' }
              ])
            },
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6
          },
          {
            name: '流出',
            type: 'line',
            data: this.outflow,
            smooth: true,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(0,128,255,0.25)' },
                { offset: 1, color: 'rgba(5,20,35,0.02)' }
              ])
            },
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6
          }
        ]
      }
      if (update) this.charts.c1.setOption(option)
      else this.charts.c1.setOption(option)
    },

    _setChart2(update = false) {
      // Top-right: 水库水位（m）折线 + 标注当前值
      const latest = this.reservoirLevel[this.reservoirLevel.length - 1] || 0
      const option = {
        title: { text: `${this.displayStationName}水位（m）`, left: 'left', textStyle: { color: '#9bdcff', fontSize: 12 } },
        tooltip: { trigger: 'axis' },
        grid: { left: '10%', right: '6%', bottom: '10%', top: '26%' },
        xAxis: {
          type: 'category',
          data: this.times,
          axisLabel: { color: '#9bdcff', interval: 2 },
          axisLine: { lineStyle: { color: '#12384c' } }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: '{value} m', color: '#9bdcff' },
          splitLine: { lineStyle: { color: 'rgba(10,30,40,0.6)' } }
        },
        series: [{
          data: this.reservoirLevel,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 3 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0,242,248,0.2)' },
              { offset: 1, color: 'rgba(0,10,20,0.02)' }
            ])
          },
          markPoint: {
            data: [
              { type: 'max', name: '最高' },
              { type: 'min', name: '最低' },
            ],
            label: { color: '#fff' }
          }
        }]
      }
      if (update) this.charts.c2.setOption(option)
      else this.charts.c2.setOption(option)
    },

    _setChart3(update = false) {
      // Bottom-left: 按行业的水量分配（柱状图）
      const option = {
        title: { text: '按行业分配（万 m³）', left: 'left', textStyle: { color: '#9bdcff', fontSize: 12 } },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '8%', right: '6%', bottom: '12%', top: '22%' },
        xAxis: {
          type: 'category',
          data: this.allocationBySector.categories,
          axisLine: { lineStyle: { color: '#123844' } },
          axisLabel: { color: '#cfeefc' }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#cfeefc' },
          splitLine: { lineStyle: { color: '#06262b' } }
        },
        series: [{
          name: '分配量',
          type: 'bar',
          data: this.allocationBySector.values,
          barWidth: '40%',
          itemStyle: {
            borderRadius: [6, 6, 2, 2],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0,242,248,1)' },
              { offset: 1, color: 'rgba(0,128,255,0.6)' }
            ])
          }
        }]
      }
      if (update) this.charts.c3.setOption(option)
      else this.charts.c3.setOption(option)
    },

    _setChart4(update = false) {
      // Bottom-right: 饼图（占比：水源或用途）
      const option = {
        title: { text: '水源构成（%）', left: 'center', top: 6, textStyle: { color: '#9bdcff', fontSize: 12 } },
        tooltip: { trigger: 'item', formatter: '{a} <br/>{b}: {c}% ({d}%)' },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'center',
          textStyle: { color: '#cfeefc' }
        },
        series: [{
          name: '水源占比',
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['65%', '55%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            formatter: '{b}\n{c}%',
            color: '#e6fbff'
          },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 'bold' }
          },
          itemStyle: {
            borderColor: '#081a20',
            borderWidth: 2
          },
          data: this.allocationPie
        }]
      }
      if (update) this.charts.c4.setOption(option)
      else this.charts.c4.setOption(option)
    }
  }
}
</script>

<style scoped>

.border {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(8, 18, 32, 0.96), rgba(13, 38, 50, 0.94));
  border-radius: 10px;
  margin-top: 0;
  padding: 18px;
  box-sizing: border-box;
  color: #eef7ff;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(99, 179, 237, 0.28);
  box-shadow: 0 18px 52px rgba(0, 0, 0, 0.42);
}

.orb-business-panel {
  width: min(1040px, calc(100vw - 230px));
  height: calc(100vh - 150px);
  position: absolute;
  left: 50%;
  top: 112px;
  transform: translateX(-54%);
  z-index: 9998;
}

/* header */
.header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.header-text {
  flex: 1;
}
.card-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}
.close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
}
.close:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.28);
}

/* charts wrapper: 2x2 grid */
.charts-wrapper{
  width: 100%;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10px;
  padding: 8px;               /* 内边距可控 */
  box-sizing: border-box;
  overflow: hidden;
}
.query-summary {
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid rgba(56, 189, 248, 0.16);
  border-radius: 7px;
  background: rgba(56, 189, 248, 0.08);
}
.summary-main {
  display: flex;
  align-items: baseline;
  gap: 10px;
  color: #eef7ff;
}
.summary-station {
  font-size: 15px;
  font-weight: 700;
  color: #76d9ff;
}
.summary-metric {
  font-size: 12px;
  color: rgba(125, 211, 252, 0.76);
}
.summary-main strong {
  font-size: 20px;
  color: #fff;
}
.summary-meta {
  display: flex;
  gap: 16px;
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.58);
}
/* each chart box */
.chart {
  width: 100%;
  height: 100%;
  min-height: 0;               /* 🔥 防止 flex/grid 内容溢出 */
  padding: 4px;
  border-radius: 8px;
  overflow: hidden;            /* 🔥 图表再保险不溢出 */
  box-sizing: border-box;
}

/* small responsive tweaks */
@media (max-width: 1200px) {
  .charts-wrapper { grid-template-rows: 1fr 1fr; gap: 8px; }
}
</style>
